/**
 * Generate a JPEG poster image from an MP4 file using an off-DOM video and canvas.
 * @param {File} file
 * @param {AbortSignal|null} signal
 * @returns {Promise<{ posterFile: File, previewUrl: string, width: number, height: number }>}
 */
const VIDEO_POSTER_TIMEOUT_MS = 30000;

const generateVideoPoster = ( file, signal = null ) => new Promise( ( resolve, reject ) => {
    if ( !( file instanceof File ) ) {
        reject( new Error( "Invalid video file." ) );
        return;
    }

    if ( signal?.aborted ) {
        reject( new Error( "Video preview creation was cancelled." ) );
        return;
    }

    if ( typeof URL === "undefined" || typeof URL.createObjectURL !== "function" ) {
        reject( new Error( "Video preview creation is unavailable." ) );
        return;
    }

    let objectUrl;
    try {
        objectUrl = URL.createObjectURL( file );
    } catch ( error ) {
        reject( error instanceof Error ? error : new Error( String( error ) ) );
        return;
    }

    let video;
    try {
        video = document.createElement( "video" );
    } catch ( error ) {
        try {
            URL.revokeObjectURL?.( objectUrl );
        } catch ( cleanupError ) {
            // The construction error remains authoritative.
        }
        reject( error instanceof Error ? error : new Error( String( error ) ) );
        return;
    }
    let settled = false;
    let timeoutId = null;
    let onAbort = null;
    let onError = null;
    let onLoadedMetadata = null;
    let onSeeked = null;
    let canvas = null;

    const safely = callback => {
        try {
            callback();
        } catch ( error ) {
            // Cleanup is best-effort; every remaining cleanup step must still run.
        }
    };

    const cleanup = () => {
        if ( timeoutId !== null ) safely( () => clearTimeout( timeoutId ) );
        if ( onAbort ) safely( () => signal?.removeEventListener( "abort", onAbort ) );
        if ( onError ) safely( () => video.removeEventListener( "error", onError ) );
        if ( onLoadedMetadata ) safely( () => video.removeEventListener( "loadedmetadata", onLoadedMetadata ) );
        if ( onSeeked ) safely( () => video.removeEventListener( "seeked", onSeeked ) );
        safely( () => video.pause() );
        safely( () => video.removeAttribute( "src" ) );
        safely( () => video.load() );
        if ( canvas !== null ) {
            safely( () => { canvas.width = 1; } );
            safely( () => { canvas.height = 1; } );
            canvas = null;
        }
        if ( typeof URL.revokeObjectURL === "function" ) {
            safely( () => URL.revokeObjectURL( objectUrl ) );
        }
    };

    const fail = error => {
        if ( settled ) return;
        settled = true;
        cleanup();
        reject( error instanceof Error ? error : new Error( String( error ) ) );
    };

    const capture = () => {
        if ( settled ) return;

        try {
            const width = video.videoWidth;
            const height = video.videoHeight;
            if ( width < 1 || height < 1 ) {
                fail( new Error( "Video metadata is unavailable." ) );
                return;
            }

            canvas = document.createElement( "canvas" );
            canvas.width = width;
            canvas.height = height;

            const context = canvas.getContext( "2d" );
            if ( context === null ) {
                fail( new Error( "Canvas rendering is unavailable." ) );
                return;
            }

            context.drawImage( video, 0, 0, width, height );
            canvas.toBlob( blob => {
                if ( settled ) return;

                try {
                    if ( !( blob instanceof Blob ) || blob.type !== "image/jpeg" ) {
                        fail( new Error( "Unable to generate a poster image." ) );
                        return;
                    }

                    const basename = file.name.replace( /\.[^.]+$/, "" ) || "video";
                    const posterFile = new File( [ blob ], `${ basename }-poster.jpg`, { type: "image/jpeg" } );
                    const previewUrl = URL.createObjectURL( posterFile );

                    settled = true;
                    cleanup();
                    resolve( { posterFile, previewUrl, width, height } );
                } catch ( error ) {
                    fail( error );
                }
            }, "image/jpeg", 0.85 );
        } catch ( error ) {
            fail( error );
        }
    };

    onAbort = () => fail( new Error( "Video preview creation was cancelled." ) );
    onError = () => fail( new Error( "Unable to load the selected video." ) );
    onLoadedMetadata = () => {
        try {
            const duration = Number.isFinite( video.duration ) ? video.duration : 0;
            const targetTime = duration > 1 ? 1 : 0;

            if ( targetTime === 0 ) {
                capture();
                return;
            }

            onSeeked = () => capture();
            video.addEventListener( "seeked", onSeeked, { once: true } );
            video.currentTime = targetTime;
        } catch ( error ) {
            if ( onSeeked ) safely( () => video.removeEventListener( "seeked", onSeeked ) );
            onSeeked = null;
            capture();
        }
    };

    try {
        signal?.addEventListener( "abort", onAbort, { once: true } );
        video.addEventListener( "error", onError, { once: true } );
        video.addEventListener( "loadedmetadata", onLoadedMetadata, { once: true } );
        timeoutId = setTimeout( () => fail( new Error( "Video preview creation timed out." ) ), VIDEO_POSTER_TIMEOUT_MS );
        video.preload = "metadata";
        video.muted = true;
        video.playsInline = true;
        video.crossOrigin = "anonymous";
        video.src = objectUrl;
    } catch ( error ) {
        fail( error );
        return;
    }

    if ( signal?.aborted ) onAbort();
} );

export default generateVideoPoster;
